import sum from 'lodash/sum';

import {
  Column,
  DataTable,
  ColumnModel,
  Button,
  Icons,
  ShadowDivider,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';

import { Money } from 'components/money';
import { useWorkloadTrafficDestinationsHistoryDrawer } from 'features/cluster/cost-report/network-cost/_hooks/useWorkloadTrafficDestinationsHistoryDrawer';
import { useCostReportContext } from 'features/cost-report/hooks/useCostReportContext';
import { getNetworkCostPrecision } from 'features/cost-report/utils/networkCosts';

import { CostDetailsTooltip } from './CostDetailsTooltip';
import { TrafficCell } from './TrafficCell';
import { TrafficRatioBars } from './TrafficRatioBars';
import { DataTransferTimeSeries } from '../../types/networkCostOverTime';

type TableData = {
  name: 'intra-az' | 'cross-az' | 'internet' | 'cross-region' | 'cloud-api';
  egressCost: string;
  egressTraffic: string;
  ingressCost: string;
  ingressTraffic: string;
};

type Props<T extends DataTransferTimeSeries[number] = any> = {
  data: T;
  columnModels: ColumnModel[];
  showDetails?: boolean;
};

export const ExpandedTable = ({ data, columnModels, showDetails }: Props) => {
  const tableData: TableData[] = [
    {
      name: 'intra-az',
      egressCost: data.egress.intraZoneCost,
      egressTraffic: data.egress.intraZoneBytes,
      ingressCost: data.ingress.intraZoneCost,
      ingressTraffic: data.ingress.intraZoneBytes,
    },
    {
      name: 'cross-az',
      egressCost: data.egress.interZoneCost,
      egressTraffic: data.egress.interZoneBytes,
      ingressCost: data.ingress.interZoneCost,
      ingressTraffic: data.ingress.interZoneBytes,
    },
    {
      name: 'internet',
      egressCost: data.egress.internetCost,
      egressTraffic: data.egress.internetBytes,
      ingressCost: data.ingress.internetCost,
      ingressTraffic: data.ingress.internetBytes,
    },
    {
      name: 'cross-region',
      egressCost: data.egress.interRegionCost,
      egressTraffic: data.egress.interRegionBytes,
      ingressCost: data.ingress.interRegionCost,
      ingressTraffic: data.ingress.interRegionBytes,
    },
    {
      name: 'cloud-api',
      egressCost: data.egress.cloudApiCost,
      egressTraffic: data.egress.cloudApiBytes,
      ingressCost: data.ingress.cloudApiCost,
      ingressTraffic: data.ingress.cloudApiBytes,
    },
  ];

  // calculates first column width
  const computedWidth = sum(
    columnModels.slice(0, columnModels.length - 3).map((d) => d.computedWidth)
  );

  const { open } = useWorkloadTrafficDestinationsHistoryDrawer();
  const { currentDateRange, adjustedDateRange, timezone } =
    useCostReportContext();

  return (
    <Box data-testid="network-zone-details" position="relative">
      <DataTable
        data={tableData}
        maxHeight={310}
        sizing="l"
        sx={{
          '&& .DS-Table-BodyRowWrapper': {
            backgroundColor: tokens.theme.bg.structural.surfaceRaised,
            borderBottom: tokens.theme.border.structural.neutral,
            '& .DS-Table-Row:hover': {
              backgroundColor: tokens.theme.bg.action.surfaceHover,
            },
          },
        }}
        onRowClick={(e) => {
          e?.stopPropagation();
          e?.preventDefault();
        }}
      >
        <Column<TableData>
          id="name"
          width={computedWidth}
          cellWrapperSx={{
            justifyContent: 'flex-end',
          }}
          bodyCellSx={{ typography: 'L12B' }}
        />

        <Column<TableData>
          id="traffic"
          width={columnModels[columnModels.length - 3].computedWidth}
          renderCell={(row) => {
            return (
              <TrafficCell
                egress={+row.egressTraffic}
                ingress={+row.ingressTraffic}
                stackProps={{ width: '50%' }}
              />
            );
          }}
        />
        <Column<TableData>
          id="cost"
          minWidth={columnModels[columnModels.length - 2].computedWidth}
          renderCell={(row) => {
            const egress = +row.egressCost;
            const ingress = +row.ingressCost;
            const total = egress + ingress;
            const precision = getNetworkCostPrecision(total);
            const showRatio = row.name === 'cross-az'; // only cross AZ ingress traffic costs

            return (
              <Stack direction="row" justifyContent="space-between">
                <CostDetailsTooltip
                  egress={egress}
                  ingress={ingress}
                  disable={!showRatio}
                >
                  <Stack gap={1} width="120px">
                    <Typography variant="P12R">
                      <Money
                        amount={egress + ingress}
                        precision={precision}
                        fixed={precision}
                      />
                    </Typography>

                    {showRatio && (
                      <TrafficRatioBars
                        egress={egress}
                        ingress={ingress}
                        size="md"
                      />
                    )}
                  </Stack>
                </CostDetailsTooltip>
                {showDetails && showRatio && (
                  <Button
                    variant="text"
                    size="md"
                    endIcon={<Icons.ArrowRight />}
                    onClick={() =>
                      open({
                        workloadName: data.workloadName,
                        workloadType: data.workloadType,
                        namespace: data.namespace,
                        range: currentDateRange,
                        adjustedRange: adjustedDateRange,
                        timezone,
                      })
                    }
                  >
                    View details
                  </Button>
                )}
              </Stack>
            );
          }}
        />
      </DataTable>
      <ShadowDivider
        size={16}
        opacity={0.3}
        sx={{ height: '100%', position: 'absolute', top: 0, left: 0 }}
      />
    </Box>
  );
};
